import type { EntityType, FileItem } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, X } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { formatMoment, formatSize } from '@/lib/format';
import { commentsQuery } from '@/lib/queries';
import { Avatar, Button, Spinner } from './ui';

/** Комментарии с вложениями к любой записи: сотруднику, задаче, позже — сделке и тикету. */
export function Comments({ entityType, entityId }: { entityType: EntityType; entityId: string }) {
  const qc = useQueryClient();
  const list = useQuery(commentsQuery(entityType, entityId));
  const [files, setFiles] = useState<FileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const send = useMutation({
    mutationFn: (body: string) =>
      api('/comments', {
        method: 'POST',
        body: { entityType, entityId, body, fileIds: files.map((f) => f.id) },
      }),
    onSuccess: async () => {
      formRef.current?.reset();
      setFiles([]);
      await qc.invalidateQueries({ queryKey: ['comments', entityType, entityId] });
    },
    onError: (err) => setError(err.message),
  });

  async function upload(list: FileList | null) {
    if (!list?.length) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(list)) {
        const fd = new FormData();
        fd.append('file', file);
        const item = await api<FileItem>('/files', { method: 'POST', body: fd });
        setFiles((prev) => [...prev, item]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = String(new FormData(e.currentTarget).get('body') ?? '').trim();
    if (!body) return;
    send.mutate(body);
  }

  return (
    <section className="panel">
      <h2 className="border-b border-line px-5 py-3.5 font-semibold">Комментарии</h2>
      <div className="px-5 py-4">
        {list.isPending ? (
          <Spinner />
        ) : list.data?.length ? (
          <ul className="flex flex-col gap-4">
            {list.data.map((c) => (
              <li key={c.id} className="flex gap-3">
                <Avatar name={c.author.fullName} userId={c.author.userId} size={30} />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px]">
                    <span className="font-medium">{c.author.fullName}</span>
                    <span className="ml-2 text-steel">{formatMoment(c.createdAt)}</span>
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap break-words">{c.body}</p>
                  {c.files.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {c.files.map((f) => (
                        <li key={f.id}>
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded border border-line px-2 py-1 text-[13px] hover:bg-sunken"
                          >
                            <Paperclip className="size-3.5 text-steel" aria-hidden />
                            {f.name}
                            <span className="text-steel">{formatSize(f.size)}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-steel">Пока без комментариев.</p>
        )}

        <form ref={formRef} onSubmit={onSubmit} className="mt-5">
          <label htmlFor="comment-body" className="sr-only">
            Комментарий
          </label>
          <textarea
            id="comment-body"
            name="body"
            rows={3}
            placeholder="Написать комментарий"
            className="input h-auto py-2"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) formRef.current?.requestSubmit();
            }}
          />
          {files.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {files.map((f) => (
                <li
                  key={f.id}
                  className="inline-flex items-center gap-1.5 rounded bg-sunken px-2 py-1 text-[13px]"
                >
                  {f.name}
                  <button
                    type="button"
                    aria-label={`Убрать ${f.name}`}
                    onClick={() => setFiles((p) => p.filter((x) => x.id !== f.id))}
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p role="alert" className="mt-2 text-[13px] text-danger">
              {error}
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <Button type="submit" size="sm" loading={send.isPending}>
              Отправить
            </Button>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="sr-only"
              id="comment-files"
              onChange={(e) => upload(e.target.files)}
            />
            <Button variant="ghost" size="sm" loading={uploading} onClick={() => fileInput.current?.click()}>
              <Paperclip className="size-4" aria-hidden />
              Прикрепить файл
            </Button>
            <span className="ml-auto hidden text-[12px] text-steel sm:inline">Ctrl + Enter — отправить</span>
          </div>
        </form>
      </div>
    </section>
  );
}
